import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import HomePage from "./pages/HomePage";
import NotFound from "./pages/NotFound";
import LoginPage from "./pages/LoginPage";
import RegistrationPage from "./pages/RegistrationPage";
import TeacherApp from "./pages/TeacherApp";
import SuperAdminPage from "./pages/SuperAdminPage";
import OnAuthSuccess from "./pages/OnAuthSuccess";
import ResetPassword from "./pages/ResetPassword";
import SettingsPage from "./pages/SettingsPage";
import GameJoinPage from "@/components/gameify/GameJoinPage";

const queryClient = new QueryClient();

const AppRoutes = () => {
  const { user, isLoading, isSuperAdmin, adminStatus } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50" data-id="0wlxge4dc" data-path="src/App.tsx">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600" data-id="tupkmm7wh" data-path="src/App.tsx"></div>
      </div>);

  }

  // Super admins get the SuperAdminPage with super admin features
  // Level 2 admins get the TeacherApp but with admin access to all features
  // Regular users get the TeacherApp with tier-based restrictions

  return (
    <Routes data-id="vjua4paoh" data-path="src/App.tsx">
      {/* Auth routes - accessible regardless of authentication status */}
      <Route path="/login" element={<LoginPage data-id="vf193d0e3" data-path="src/App.tsx" />} data-id="3z31s64a1" data-path="src/App.tsx" />
      <Route path="/register" element={<RegistrationPage data-id="ygzw8xkz0" data-path="src/App.tsx" />} data-id="38e6wjb1u" data-path="src/App.tsx" />
      <Route path="/onauthsuccess" element={<OnAuthSuccess data-id="dxm3godtm" data-path="src/App.tsx" />} data-id="n8znb2h4a" data-path="src/App.tsx" />
      <Route path="/resetpassword" element={<ResetPassword data-id="ecq01xo72" data-path="src/App.tsx" />} data-id="qqcc6eb5e" data-path="src/App.tsx" />
      
      {/* Game join routes - accessible to everyone */}
      <Route path="/game-join" element={<GameJoinPage data-id="mh2xd62sy" data-path="src/App.tsx" />} data-id="bjjtqaa6h" data-path="src/App.tsx" />
      <Route path="/game-join/:roomCode" element={<GameJoinPage data-id="gzbfb4i5e" data-path="src/App.tsx" />} data-id="9awjynecm" data-path="src/App.tsx" />

      {user ?
      <>
          {isSuperAdmin ?
        <>
              <Route path="/" element={<SuperAdminPage data-id="yjzulo0u7" data-path="src/App.tsx" />} data-id="lw5fdzb2c" data-path="src/App.tsx" />
              <Route path="/dashboard" element={<SuperAdminPage data-id="ymhnmyx29" data-path="src/App.tsx" />} data-id="1ffd90lh2" data-path="src/App.tsx" />
              <Route path="/superadmin" element={<SuperAdminPage data-id="rfo7y7f57" data-path="src/App.tsx" />} data-id="05i1fsox3" data-path="src/App.tsx" />
              <Route path="/app" element={<TeacherApp data-id="zga30gjfh" data-path="src/App.tsx" />} data-id="26jprxmcg" data-path="src/App.tsx" />
              <Route path="/settings" element={<SettingsPage data-id="csk806d43" data-path="src/App.tsx" />} data-id="cauxgvc5d" data-path="src/App.tsx" />
            </> :

        <>
              <Route path="/" element={<TeacherApp data-id="70jzbjybv" data-path="src/App.tsx" />} data-id="ohmxn3r75" data-path="src/App.tsx" />
              <Route path="/dashboard" element={<TeacherApp data-id="dkt2o86ut" data-path="src/App.tsx" />} data-id="jaqcizde2" data-path="src/App.tsx" />
              <Route path="/app" element={<TeacherApp data-id="ejz921vxz" data-path="src/App.tsx" />} data-id="p382c8lvx" data-path="src/App.tsx" />
              <Route path="/superadmin" element={<TeacherApp data-id="q5nbdi6f1" data-path="src/App.tsx" />} data-id="env8y4fcl" data-path="src/App.tsx" />
              <Route path="/settings" element={<SettingsPage data-id="vyj2iawcw" data-path="src/App.tsx" />} data-id="vt6psfa3x" data-path="src/App.tsx" />
            </>
        }
        </> :

      <>
          <Route path="/" element={<HomePage data-id="7pr3coce4" data-path="src/App.tsx" />} data-id="2hjfp285p" data-path="src/App.tsx" />
          <Route path="/dashboard" element={<LoginPage data-id="c7c0mpccw" data-path="src/App.tsx" />} data-id="cf6nd10y8" data-path="src/App.tsx" />
          <Route path="/app" element={<LoginPage data-id="ggqz9maam" data-path="src/App.tsx" />} data-id="11atqbkda" data-path="src/App.tsx" />
          <Route path="/superadmin" element={<LoginPage data-id="t65so2sqv" data-path="src/App.tsx" />} data-id="oef8la33g" data-path="src/App.tsx" />
          <Route path="/settings" element={<LoginPage data-id="q4s3ri3ri" data-path="src/App.tsx" />} data-id="jktf8ffqy" data-path="src/App.tsx" />
        </>
      }
      
      {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
      <Route path="*" element={<NotFound data-id="dlx95fs2c" data-path="src/App.tsx" />} data-id="su8zn090j" data-path="src/App.tsx" />
    </Routes>);

};

const App = () =>
<QueryClientProvider client={queryClient} data-id="4ghkj206u" data-path="src/App.tsx">
    <TooltipProvider data-id="l68crbncr" data-path="src/App.tsx">
      <AuthProvider data-id="sl4i5io7c" data-path="src/App.tsx">
        <Toaster data-id="bu6f5xl3f" data-path="src/App.tsx" />
        <BrowserRouter data-id="k3tc8g67o" data-path="src/App.tsx">
          <AppRoutes data-id="udmjdwvn8" data-path="src/App.tsx" />
        </BrowserRouter>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>;


export default App;